import React, { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAppStore } from "./store";
import { useThemeStore } from "./store/theme";
import { Suspense } from "react";
// Coming-soon mode: login/signup show the Coming Soon page, but links people
// open from their inbox or a TV's QR code still have to work.
const ForgotPassword = React.lazy(() => import("./pages/Auth/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/Auth/ResetPassword"));
const AuthAction = React.lazy(() => import("./pages/Auth/AuthAction"));
const TvLink = React.lazy(() => import("./pages/Auth/TvLink"));
import Layout from "./components/Layout";
import ComingSoon from "./pages/ComingSoon";
import ProtectedRoute from "./components/ProtectedRoute";
import MobileLayout from "./components/MobileLayout";
import MobileAppDetector from "./components/MobileAppDetector";
import { DEVELOPMENT } from "./config/config";
import { getLocalData, saveLocalData } from "./api";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { BlockedUserOverlay } from "./components/BlockedUserOverlay";

import "./App.css";

function App() {
  const { theme } = useThemeStore();
  const { isLoggedIn, checkAuthStatus, initializeMockUser, user, setUser } =
    useAppStore();
  const navigate = useNavigate();

  const initSession = async () => {
    const storage = await getLocalData("idearify-app-storage");
    const storedUser = storage?.state?.user;

    // Prioritize local storage user, fallback to in-memory user
    const currentUser = storedUser || user;

    if (!currentUser) {
      console.error("Cannot initialize session: user is null.");
      return null;
    }

    // Update storage only if we used the fallback state user
    if (!storedUser) {
      await saveLocalData("idearify-app-storage", {
        ...storage,
        state: { ...(storage?.state || {}), user: currentUser },
      });
    }

    // Ensure application state matches
    if (user !== currentUser) {
      setUser(currentUser);
    }

    return currentUser;
  };

  useEffect(() => {
    // Apply theme to document
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
  }, [theme]);

  useEffect(() => {
    // Check authentication status on app load
    const hasValidSession = checkAuthStatus(initSession);

    // If user has valid session and is on auth pages, redirect to home
    if (
      hasValidSession &&
      window.location.pathname.match(
        /^\/(login|signup|forgot-password|reset-password)/,
      )
    ) {
      // Use setTimeout to ensure navigation happens after initial render
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 0);
    }

    // Initialize mock user in development
    if (process.env.NODE_ENV === "development" && DEVELOPMENT) {
      initializeMockUser();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initSession is recreated each render; this must run once per auth check, not per render
  }, [checkAuthStatus, navigate, initializeMockUser]);

  return (
    <MobileAppDetector
      autoRedirect={false} // Set to true if you want automatic redirection
      showBanner={true}
      bannerDelay={3000}
    >
      <Helmet>
        <title>Idearify - Share Your Ideas</title>
        <meta
          name="description"
          content="Share and discover innovative ideas with the Idearify community"
        />
        <meta
          name="theme-color"
          content={theme === "dark" ? "#1A1A1A" : "#F8F8FF"}
        />
        {/* Mobile app meta tags */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="application-name" content="Idearify" />
      </Helmet>

      <UpdatePrompt />
      <BlockedUserOverlay />

      <div className={`app ${theme}`}>
        <Suspense fallback={<div className="flex h-screen w-full items-center justify-center bg-[#F8F8FF] dark:bg-[#1A1A1A]"><div className="animate-pulse flex flex-col items-center"><div className="w-12 h-12 rounded-full border-4 border-t-blue-500 border-gray-200 animate-spin mb-4"></div><div className="text-gray-500">Loading...</div></div></div>}>
          <Routes>
            {/* Public routes */}
            {/* Coming-soon mode: signed-out visitors opening this directly
                (e.g. after the update prompt's ?v= reload) get Coming Soon. */}
            <Route
              path="/mobile-layout"
              element={isLoggedIn ? <MobileLayout /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/login"
              element={!isLoggedIn ? <ComingSoon /> : <Navigate to="/" replace />}
            />
            <Route
              path="/signup"
              element={!isLoggedIn ? <ComingSoon /> : <Navigate to="/" replace />}
            />
            <Route
              path="/forgot-password"
              element={
                !isLoggedIn ? <ForgotPassword /> : <Navigate to="/" replace />
              }
            />
            <Route
              path="/reset-password"
              element={
                !isLoggedIn ? <ResetPassword /> : <Navigate to="/" replace />
              }
            />
            <Route path="/auth/action" element={<AuthAction />} />
            {/* Opened by Idearify TV's sign-in QR code, usually on a phone.
                Only already signed-in users can approve a TV; nobody can sign
                in while we're in coming-soon mode. */}
            <Route
              path="/tv-link"
              element={isLoggedIn ? <TvLink /> : <ComingSoon />}
            />
            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </div>
    </MobileAppDetector>
  );
}

export default App;
